import Vue from 'vue';
import { Module } from 'vuex';
import { RootState } from '@/types/store';
import {
  serverDateNow,
  dateParse,
  isUndef,
  dateFormat,
  dateOffset,
  dateNow,
  wait,
} from '@/lib/utils';
import { chooseWXPay } from '@/lib/script/pay';
import { ActiveActivityId, STATIC_HOST } from '@/lib/constant';
import { log } from 'mathjs';

/** 可换套餐期限 */
function upgradeDeadline(basicInfo: Activity.BasicInfo) {
  const joined = basicInfo ? basicInfo.joined : false;
  if (joined && basicInfo!.can_change_package) {
    const now = dateNow().getTime();
    const threeDays = dateOffset(dateParse(basicInfo!.join_time), 3);
    const finish = threeDays.getTime();
    const leftSecond = Math.floor((finish - now) / 1000);
    if (leftSecond > 0) {
      const arr = dateFormat(threeDays).split('-');
      return `限${Number(arr[1])}月${Number(arr[2])}日前`;
    } else {
      return null;
    }
  } else {
    return null;
  }
}

export interface ActivieyState {
  Optional_goods: [];
  topicId: string; // 判断是否是情侣跑，还是亲子跑
  /** 活动id */
  activityId: string;
  /** 活动基本信息 */
  basicInfo: {
    [activityId: string]: Activity.BasicInfo | undefined;
  };
  /** 活动Level信息 */
  activityIdMapLevels: {
    [activityId: string]: Activity.Level[] | undefined;
  };
  /** 活动评价信息 */
  comments: {
    [activityId: string]: Activity.Comment[] | undefined;
  };
  /** 活动最新战报信息 */
  activityNews: {
    [activityId: string]: ListWrap<Activity.New> | undefined;
  };
  newsRequesing: boolean;
  /** 搜索关键字 */
  keyword: string;
  /** 搜索结果 */
  searchResult: {
    [name: string]: ListWrap<number> | undefined;
  };
  /** 是否正在搜索中 */
  searchRequesting: boolean;
  /** 活动level对应的战队信息 key由activityId_levelId 拼接而来 */
  teamList: {
    [key: string]: ListWrap<number> | undefined;
  };
  teamListRequesting: boolean;
  /** team信息 */
  team: {
    card: {
      [teamId: number]: Activity.TeamListItem | undefined;
    };
    detail: {
      [teamId: number]: Activity.TeamDetail | undefined;
    };
    detailPromise: {
      [teamId: number]: Promise<Activity.TeamDetail> | undefined;
    };
  };
  /** 活动套餐 */
  packageMap: {
    [packageId: number]: Activity.Package | undefined;
  };
  activityPackages: {
    [activityId_activitySpecialId: string]: number[] | undefined;
  };
  /** 活动礼品 */
  giftMap: {
    [giftId: number]: Activity.GiftItem | undefined;
  };
  activityGift: {
    [activityId: string]: Activity.Gift | undefined;
  };
  /** 活动广告 */
  activityAd: {
    [activityId_type: string]: Activity.Ad[] | undefined;
  };
  /** 个人邀请卡 */
  personalInviteCard: {
    [activityId: string]: string;
  };
  /** 总招募信息 */
  recruitStatMap: {
    [activityId: string]: Activity.RecruitStat | undefined;
  };
  /** 活动锦囊 */
  activityTipsMap: {
    [activityId: string]: Activity.Tip | undefined;
  };
  shareWord: {
    [activityId_kind: string]: Activity.ShareWord[] | undefined;
  };
  /** 报名信息map */
  registrationMap: {
    [activityId: string]: Activity.Registration | undefined;
  };
  categoryList: Activity.Category[] | null;
  questionList: {
    [categoryId: number]: Activity.QA[] | undefined;
  };
  /** 百日跑成就数据 key */
  achievementMap: {
    [activityId: string]:
      | {
          [userId: string]: Activity.Summary | undefined;
        }
      | undefined;
  };
  /** 610首页展示数据 */
  indexData: Activity.IndexData;
  /** 610首页报名开启信息 */
  registrationOpenInfo: Activity.RegistrationOpenInfo;
  /** 610首页banner信息 */
  bannerInfo: Banner[] | null;
  /** 列表banner */
  activityListBanner: Banner[] | null;
  /** 是否显示加入引导弹窗 */
  joinHint: boolean;

  /** 第十届完赛报告 */
  report10: Activity.Report10 | null;
  /** 第11届完赛报告 */
  report11: Activity.Report10 | null;
  /** 完赛报告 */
  report: {
    [activityId: string]: Activity.Report10;
  };

  /** 用户列表 */
  runners: {
    list: Array<{ avatar: string; nickname: string }>;
    loaded: boolean;
    requesting: boolean;
  };

  /** 是否同意用户协议 */
  isSelectedAgreement: boolean;
  /** 当前报名队伍信息 */
  currentTeam: {
    [userId: number]: Activity.CurrentTeam;
  };
  story: Activity.Story[] | null;
  hasJump: boolean;
  /** 用户搜索结果 */
  searchUser: {
    [name: string]: ListWrap<Activity.SearchUserResult> & ListRequesting;
  };
  special_type: string;
  selectSkuMap: Record<number, number> | null
//   termCard: 
}

const m: Module<ActivieyState, RootState> = {
  namespaced: true,
  state: () => ({
    Optional_goods: [], // 可选商品
    activityId: '',
    basicInfo: {},
    activityIdMapLevels: {},
    teamListRequesting: false,
    team: {
      card: {},
      detail: {},
      detailPromise: {},
    },
    topicId: '', // 专题id
    keyword: '',
    searchResult: {},
    searchRequesting: false,
    teamList: {},
    comments: {},
    activityNews: {},
    newsRequesing: false,
    packageMap: {},
    activityPackages: {},
    giftMap: {},
    activityGift: {},
    activityAd: {},
    personalInviteCard: {},
    recruitStatMap: {},
    activityTipsMap: {},
    shareWord: {},
    participationInfo: {},
    participationList: null,
    registrationMap: {},
    categoryList: null,
    questionList: {},
    achievementMap: {},
    indexData: {
      sign_in_count: 0,
      insist_count: 0,
      team_count: 0,
    },
    registrationOpenInfo: [],
    bannerInfo: null,
    activityListBanner: null,
    joinHint: true,
    report10: null,
    report11: null,
    report: {},
    runners: {
      list: [],
      requesting: false,
      loaded: false,
    },
    isSelectedAgreement: false,
    currentTeam: {},
    story: null,
    memberList: {},
    monitorList: {},
    homePage: null,
    hasJump: false,
    searchUser: {},
    special_type: '',
    selectSkuMap: null
  }),
  getters: {
    /** 主要的活动id */
    activityId(): string {
    //   return '31';
    return ActiveActivityId!
    },
    /** 邀请活动id，邀请卡页面中使用的活动id */
    inviteActivityId(): string {
      return ActiveActivityId!;
    },
    /** @deprecated 打卡活动id，打卡页面中使用的活动id */
    signInActivityId(): string {
      return ActiveActivityId!;
    },
    /** 队长活动id */
    monitorActivityId(): string {
      return ActiveActivityId!;
    },
    /** 当前所有执行中的活动 */
    activityIds(): string[] {
        return []
      return ['31', '24'];
    },
    /** 所有活动，可以在活动列表中使用，不需要显示的可以隐藏 */
    allActivityIds(): string[] {
      return ['60', '54', '24', '6', '5', '4', '3', '2'];
    },
    /** 所有管理的战队活动id，将在战队管理列表中显示 */
    allMonitorActivityIds(): string[] {
      return ['60', '54', '24', '6', '5', '4', '3'];
    },
  },
  mutations: {
    Optional_goods(state, data) {
      state.Optional_goods = data;
    },
    getTopicId(state, data: string) {
      // 从成功页获取专题id
      state.topicId = data;
      // state.topicId = data
    },
    // activityId(state,data){
    //   state.topicId = data
    // },

    special_type(state, data) {    // 判断是否是亲子跑，情侣跑
      state.special_type = data;
      localStorage.setItem('activityId', data);
    },
    setActivityId(state, activityId) {
      state.activityId = activityId;
    },
    setRegistrationMap(
      state,
      {
        activityId,
        info,
      }: { activityId: Activity.Id; info: Activity.Registration },
    ) {
      Vue.set(state.registrationMap, activityId, info);
    },
    setBasicInfo(
      state,
      {
        activityId,
        basicInfo,
      }: { activityId: Activity.Id; basicInfo: Activity.BasicInfo },
    ) {
      if (basicInfo) {
        const serverDate = serverDateNow()?.valueOf();

        basicInfo.id = activityId;
        basicInfo.startParticipation = basicInfo
          ? serverDate >= dateParse(basicInfo!.sign_up_open_time)?.valueOf()
          : false;
        basicInfo.closeParticipation = basicInfo
          ? serverDate >=
            dateOffset(dateParse(basicInfo!.sign_up_finish_date), 1)?.valueOf()
          : false;
        basicInfo.realCloseParticipation = basicInfo
          ? serverDate >=
            dateOffset(
              dateParse(basicInfo!.sign_up_real_finish_date),
              1,
            )?.valueOf()
          : false;
        basicInfo.startMonitorApply = basicInfo
          ? serverDate >=
            dateParse(basicInfo!.monitor_apply_start_date)?.valueOf()
          : false;
        basicInfo.closeMonitorApply = basicInfo
          ? serverDate >=
            dateOffset(
              dateParse(basicInfo!.monitor_apply_finish_date),
              1,
            )?.valueOf()
          : false;
        basicInfo.startSignIn = basicInfo
          ? serverDate >= dateParse(basicInfo!.sign_in_start_date)?.valueOf()
          : false;
        basicInfo.closeSignIn = basicInfo
          ? serverDate >=
            dateOffset(dateParse(basicInfo!.sign_in_finish_date), 1)?.valueOf()
          : false;
        basicInfo.openSignUp = basicInfo
          ? serverDate >= dateParse(basicInfo!.sign_up_open_time)?.valueOf()
          : false;
        // @deprecated: 不要再使用
        basicInfo.joinedTeamId =
          basicInfo && basicInfo.joined && basicInfo.team_id
            ? String(basicInfo.team_id)
            : null;
        basicInfo.upgradeDeadline = upgradeDeadline(basicInfo);
      }

      Vue.set(state.basicInfo, activityId, basicInfo);
    },
    setLevels(state, { activityId, levels }) {
      Vue.set(state.activityIdMapLevels, activityId, levels);
    },
    setSearchRequesing(state, requesting: boolean) {
      state.searchRequesting = requesting;
    },
    setSearchKeyWord(state, keyword: string) {
      state.keyword = keyword;
    },
    setSearchResult(
      state,
      { name, data }: { name: string; data: Activity.TeamListItem[] },
    ) {
      const ids: number[] = [];
      data.forEach((team: Activity.TeamListItem) => {
        Vue.set(state.team.card, team.id, team);
        ids.push(team.id);
      });
      state.searchResult[name]!.list = state.searchResult[name]!.list.concat(
        ids,
      );
    },
    setSearchResultLoaded(
      state,
      { name, val }: { name: string; val: boolean },
    ) {
      Vue.set(state.searchResult[name]!, 'loaded', val);
    },
    initTeamList(state, key) {
      Vue.set(state.teamList, key, {
        list: [],
        loaded: false,
      });
    },
    setTeamListRequesting(state, val: boolean) {
      state.teamListRequesting = val;
    },
    setTeamList(
      state,
      { key, data }: { key: string; data: Activity.TeamListItem[] },
    ) {
      const list: number[] = [];
      (data || []).forEach((item: Activity.TeamListItem) => {
        Vue.set(state.team.card, item.id, item);
        list.push(item.id);
      });
      state.teamList[key]!.list = state.teamList[key]!.list.concat(list);
    },
    setTeamListLoaded(state, { key, val }: { key: string; val: boolean }) {
      Vue.set(state.teamList[key]!, 'loaded', val);
    },
    setComments(
      state,
      { activityId, data }: { activityId: string; data: Activity.Comment[] },
    ) {
      Vue.set(state.comments, activityId, data);
    },
    setNews(
      state,
      { activityId, news }: { activityId: string; news: Activity.New[] },
    ) {
      const list = state.activityNews[activityId]!.list;
      // const basicInfo = state.basicInfo[activityId];
      // const num = basicInfo ? basicInfo!.total_join_count : 0;
      // const top = {
      //     id: 0,
      //     title: `第十届百日跑总人数突破${num}人！`,
      //     time: dateFormat(dateToday(), null),
      //     link: null, // 如果不存在链接此字段为null
      // };
      // if (list.length === 0 && news.length > 0) {
      //     news.unshift(top);
      // } else if (list.length === 0 && news.length === 0) {
      //     news[0] = top;
      // } else if (state.basicInfo && list.length > 0 && list[0].id === 0) {
      //     list[0] = top;
      // }
      state.activityNews[activityId]!.list = list.concat(news);
    },
    setNewsRequesting(state, requesting: boolean) {
      state.newsRequesing = requesting;
    },
    setNewsLoaded(
      state,
      { activityId, val }: { activityId: string; val: boolean },
    ) {
      Vue.set(state.activityNews[activityId]!, 'loaded', val);
    },
    /** 记录战队详情 */
    setTeamDetail(
      state,
      { teamId, data }: { teamId: number; data: Activity.TeamDetail },
    ) {
      data.id = teamId;
      Vue.set(state.team.detail, teamId, data);
    },
    /** 记录战队详情 */
    setTeamDetailPromise(state, { teamId, data }) {
      Vue.set(state.team.detailPromise, teamId, data);
    },
    /**
     * 记录礼包信息
     */
    setActivityPackage(
      state,
      { key, data }: { key: string; data: Activity.Package[] },
    ) {
      const ids: number[] = [];
      data.forEach((pack) => {
        if (pack) {
          if (pack.gift_info) {
            for (const gift of pack.gift_info) {
              gift.specification = null;
            }
          }
          Vue.set(state.packageMap, pack.id, pack);
          ids.push(pack.id);
        }
      });
      Vue.set(state.activityPackages, key, ids);
    },
    setActivityGift(
      state,
      { activityId, data }: { activityId: string; data: Activity.Gift },
    ) {
      data.gifts.forEach((item) => {
        Vue.set(state.giftMap, item.id, item);
      });
      Vue.set(state.activityGift, activityId, data);
    },
    /**
     * 记录活动广告
     */
    setActivityAd(
      state,
      {
        activityId,
        adType,
        data,
      }: { activityId: string; adType: Activity.AdType; data: Activity.Ad[] },
    ) {
      Vue.set(state.activityAd, [activityId, adType].join('_'), data);
    },
    setPersonalInviteCard(
      state,
      { activityId, card }: { activityId: string; card: string },
    ) {
      Vue.set(state.personalInviteCard, activityId, card);
    },
    /**
     * 记录总招募信息
     */
    setRecruitStat(state, { activityId, recruitStat }) {
      Vue.set(state.recruitStatMap, activityId, recruitStat);
    },
    setActivityTipsMap(
      state,
      { activityId, list }: { activityId: string; list: Activity.Tip[] },
    ) {
      Vue.set(state.activityTipsMap, activityId, list);
    },
    /**
     * 记录分享话术
     */
    setShareWord(state, { activityId, kind, shareWord }) {
      Vue.set(state.shareWord, [activityId, kind].join('_'), shareWord);
    },
    setCategoryList(state, categoryList) {
      state.categoryList = categoryList;
    },
    /**
     * 获得问题列表
     */
    setQuestionList(state, { categoryId, questionList }) {
      Vue.set(state.questionList, categoryId, questionList);
    },
    /** 记录完赛总结数据 */
    setAchievementMap(state, { activityId, userId, summary }) {
      if (summary && summary.recollection && summary.recollections) {
        summary.recollections.unshift(summary.recollection);
      }
      Vue.set(state.achievementMap[activityId]!, userId, summary);
    },
    /** 设置610首页展示数据 */
    setIndexData(state, data) {
      state.indexData = data;
    },
    /** 设置610首页banner信息 */
    setBannerInfo(state, info) {
      state.bannerInfo = info;
    },
    /** 设置610首页banner信息 */
    setActivityListBanner(state, banner) {
      state.activityListBanner = banner;
    },
    /** 设置610首页报名开启信息 */
    setRegistrationOpenInfo(state, info) {
      state.registrationOpenInfo = info;
    },
    /**
     * 设置joinHint是否可见
     */
    setJoinHintVisible(state, visible) {
      state.joinHint = visible;
    },

    /** 设置完赛10 */
    setReport10(state, data) {
      state.report10 = data;
    },
    /** 设置完赛11 */
    setReport11(state, data) {
      state.report11 = data;
    },

    /** 设置用户 */
    setRunners(state, data) {
      state.runners.list = state.runners.list.concat(data);
    },

    /** 用户请求 */
    setRunnersRequest(state, data) {
      state.runners.requesting = data;
    },

    /** 用户加载 */
    setRunnersLoaded(state, data) {
      state.runners.loaded = data;
    },

    /** 同意用户协议 */
    setSelectAgreement(state, data) {
      state.isSelectedAgreement = data;
    },
    setCurrentTeam(state, { userId, team }) {
      Vue.set(state.currentTeam, userId, team);
    },
    setStory(state, list) {
      state.story = list;
    },
    setHasJump(state, hasJump) {
      state.hasJump = hasJump;
    },
    /**
     * 搜索用户结果
     */
    initSearchUser(state, { key }) {
      Vue.set(state.searchUser, key, {
        list: [],
        loaded: false,
        requesting: false,
      });
    },
    concatSearchUser(state, { key, list }) {
      state.searchUser[key].list = state.searchUser[key].list.concat(list);
    },
    setSearchUserLoaded(state, { key, loaded }) {
      state.searchUser[key].loaded = loaded;
    },
    setSearchUserReq(state, { key, req }) {
      state.searchUser[key].requesting = req;
    },
    setSelectSkuMap(state, map) {
        state.selectSkuMap = map
    }
  },
  actions: {
    async upData({ state, commit }, { data, id, team_id}) {
      const response = await Vue.axios.post(
        '/api/v1/participation/bind_user_participation',
        {
          user_id: data, // 用户id
          activity_id: id, // 活动id
          team_id , // 战队id
        },
      );
    },
    /** 获取610首页展示数据 */
    async getIndexData({ state, commit }) {
      const response = await Vue.axios.get('/external/pc/index');
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setIndexData', rdata.data);
      }
      return rdata;
    },
    /** 获取610首页banner轮播图信息及点击跳转链接 */
    async getBannerInfo({ state, commit }, { forceUpdate } = {}) {
      // 缓存
      if (state.bannerInfo && !forceUpdate) {
        return;
      }
      const response = await Vue.axios.get('/api/v1/web/image');
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setBannerInfo', rdata.data);
      }
      return rdata;
    },
    async getActivityListBanner({ state, commit }, { forceUpdate } = {}) {
      if (state.bannerInfo && !forceUpdate) {
        return;
      }
      const response = await Vue.axios.get('/api/v1/activity/banner');
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setActivityListBanner', rdata.data);
      }
      return rdata;
    },
    /**
     * 获取活动报名情况
     * @param activityId
     * @param forceUpdate
     */
    async getRegistration(
      { state, commit },
      { activityId, forceUpdate }: { activityId: string; forceUpdate?: boolean },
    ) {
      if (state.registrationMap[activityId] && !forceUpdate) {
        return;
      }
      const response = await Vue.axios.get('/api/v1/participation/info', {
        params: {
          activity_id: String(activityId),
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setRegistrationMap', {
          activityId,
          info: rdata.data,
        });
      }
      return rdata;
    },
    /**
     * 获取活动基本信息
     * @param activityId
     * @param forceUpdate
     */
    async getBasicInfo(
      { state, commit },
      {
        activityId,
        forceUpdate,
        activitySpecialId,
        formPath
      }: {
        activityId: number;
        forceUpdate?: boolean;
        activitySpecialId?: number;
        formPath?: string
      },
    ) {
      const data = state.basicInfo[activityId];
      if (
        (data &&
        !forceUpdate &&
        String(activitySpecialId) === String(data.activity_special_id))
        || !activityId
      ) {
        return;
      }
      const response = await Vue.axios.get('/api/v1/activity/activity_info', {
        params: {
          id: activityId + '',
          activity_special_id: activitySpecialId ? activitySpecialId : undefined,
          form_path: formPath || location.href
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        const basicInfo = rdata.data;
        const url: { [id: number]: string } = {
          1: STATIC_HOST + '/meta/activity_1/main_banner.png',
          2:
            STATIC_HOST +
            '/activity_cover_image/2019-08-04-23-01-47/a3d9f183328042d89bb62af03ac9dde5.jpg',
          3: STATIC_HOST + '/meta/activity_3/main_banner.jpg',
          4: STATIC_HOST + '/meta/activity_4/activity_list_banner.jpg',
          5: STATIC_HOST + '/meta/activity_5/activity_list_banner.png',
          6: STATIC_HOST + '/meta/activity_6/activity_list_banner.jpg',
        };
        basicInfo.cover = url[Number(activityId)];
        commit('setBasicInfo', {
          activityId,
          basicInfo: rdata.data,
        });
        commit('topicId', {
          topicId: rdata.data.activity_special_type,
        });
        if (activitySpecialId) {
          commit('special_type', rdata.data.activity_special_type);
          // localStorage.setItem(
          //   "activityId",
          //   rdata.data.activity_special_type
          // );
        }
        // gio('people.set', 'activity_' + activityId, rdata.data.joined ? '1' : '0');
      }
      return rdata;
    },
    /**
     * 获取组别列表
     * @param activityId
     * @param cityId
     */
    async getLevels(
      { state, commit },
      {
        activityId,
        cityId,
        activitySpecialId,
      }: { activityId: string; cityId?: number; activitySpecialId?: number },
    ) {
      const response = await Vue.axios.get('/api/v1/activity/level_list', {
        params: {
          id: activityId,
          city_id: cityId,
          activity_special_id: activitySpecialId
            ? activitySpecialId
            : undefined,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setLevels', {
          activityId,
          levels: rdata.data,
        });
      }
      return rdata;
    },
    /**
     * 获取某个级别下的战队列表
     * @param activityId
     * @param levelId
     * @param cityId
     * @param count
     * @param offset
     * @param type 当为非空情况下，将不再显示已经满百战队
     * @param forceUpdate
     */
    async getTeamList(
      { state, commit },
      {
        activityId,
        levelId,
        count,
        offset,
        cityId,
        disShowFullTeam,
        forceUpdate,
        activitySpecialId,
        name,
      }: {
        activityId: string;
        levelId: number;
        count: number;
        offset: number;
        cityId?: number;
        disShowFullTeam: boolean;
        forceUpdate?: boolean;
        activitySpecialId?: string;
        name: string
      },
    ) {
      const key = activitySpecialId
        ? `${activityId}_${levelId}_${activitySpecialId}`
        : `${activityId}_${levelId}`;
      let wrap = state.teamList[key];
      console.log('target 请求key', key, wrap);
      if(!activityId) {
        return;
      }
      if (isUndef(wrap)) {
        commit('initTeamList', key);
        wrap = state.teamList[key];
      }
      if ((wrap!.loaded && !forceUpdate) || state.teamListRequesting) {
        return;
      }
      if (forceUpdate && wrap) {
        wrap.list = [];
        wrap.loaded = false;
      }
      const end = offset + count;
      if (end <= wrap!.list.length) {
        return;
      }
      if (offset < wrap!.list.length) {
        offset = wrap!.list.length;
        count = end - offset;
      }
      commit('setTeamListRequesting', true);
      const response = await Vue.axios.get('/api/v1/activity/team_list', {
        params: {
          id: activityId,
          activity_special_id: activitySpecialId,
          city_id: cityId,
          level_id: levelId,
          count,
          offset,
          name,
          dis_show_full_team: disShowFullTeam ? 1 : 0,
        },
      });
      const rdata = response.data;

      if (rdata.status === 0) {

        commit('setTeamList', {
          key,
          data: rdata.data,
        });
        if (rdata.data.length < count) {
          commit('setTeamListLoaded', {
            key,
            val: true,
          });
        }
      }
      commit('setTeamListRequesting', false);
      return rdata;
    },
    /**
     * 搜索战队
     * @param activityId string
     * @param count number
     * @param offset number
     * @param name string 搜索内容
     * @param forceUpdate boolean
     */
    async searchTeam(
      { state, commit },
      {
        activityId,
        count,
        offset,
        name,
        forceUpdate = false,
        activitySpecialId,
      }: {
        activityId: string;
        count: number;
        offset: number;
        name: string;
        forceUpdate: boolean;
        activitySpecialId?: string;
      },
    ) {
      const tempData = state.searchResult[name];
      if (isUndef(tempData)) {
        Vue.set(state.searchResult, name, {
          list: [],
          loaded: false,
        });
      }
      if (
        (tempData && tempData!.loaded && !forceUpdate) ||
        state.searchRequesting
      ) {
        return;
      }
      if (forceUpdate && tempData) {
        tempData.list = [];
        tempData.loaded = false;
      }
      const end = offset + count;
      if (tempData && end <= tempData.list.length) {
        return;
      }
      if (tempData && offset < tempData.list.length) {
        offset = tempData.list.length;
        count = end - offset;
      }
      commit('setSearchRequesing', true);
      const response = await Vue.axios.get('/api/v1/activity/search_team', {
        params: {
          id: activityId,
          activity_special_id: activitySpecialId,
          count,
          offset,
          name,
        },
      });
      const rdata = response.data;
      commit('setSearchRequesing', false);
      if (rdata.status === 0) {
        commit('setSearchKeyWord', name);
        commit('setSearchResult', {
          name,
          data: rdata.data,
        });
        if (rdata.data.length < count) {
          commit('setSearchResultLoaded', {
            name,
            val: true,
          });
        }
      }
      return rdata;
    },
    /**
     * 搜索用户
     */
    async searchUser({ state, commit }, { name, offset, count }) {
      const key = name;
      let wrap = state.searchUser[key];
      if (!wrap) {
        commit('initSearchUser', { key });
        wrap = state.searchUser[key];
      }
      if (wrap.requesting || wrap.loaded) {
        return;
      }
      const end = offset + count;
      const len = wrap.list.length;
      if (end <= len) {
        return;
      }
      if (offset < len) {
        offset = len;
        count = end - offset;
      }
      commit('setSearchUserReq', { key, req: true });
      const response = await Vue.axios.get('/api/v1/activity/search_user', {
        params: {
          name,
          offset,
          count,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        const list = rdata.data;
        commit('concatSearchUser', { key, list });
        if (list.length < count) {
          commit('setSearchUserLoaded', { key, loaded: true });
        }
      }
      commit('setSearchUserReq', { key, req: false });
      return rdata;
    },
    /**
     * 获取跑友说内容
     * @param activityId
     */
    async getComments({ state, commit }, activityId: string) {
      const tempData = state.comments[activityId];
      if (!isUndef(tempData)) {
        return;
      }
      const response = await Vue.axios.get(
        '/api/v1/activity/activity_comment',
        {
          params: {
            id: activityId,
          },
        },
      );
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setComments', {
          activityId,
          data: rdata.data,
        });
      }
      return rdata;
    },
    /**
     * 最新战报
     * @param activityId
     * @param count
     * @param lastId
     * @param forceUpdate
     */
    async getNews(
      { state, commit },
      {
        activityId,
        count,
        lastId,
        forceUpdate = false,
      }: {
        activityId: string;
        count: number;
        lastId: number | null;
        forceUpdate: boolean;
      },
    ) {
      if (isUndef(state.activityNews[activityId])) {
        Vue.set(state.activityNews, activityId, {
          list: [],
          loaded: false,
        });
      }
      const tempData = state.activityNews[activityId];
      if (
        state.newsRequesing ||
        (tempData && tempData.loaded && !forceUpdate)
      ) {
        return;
      }
      if (forceUpdate && tempData) {
        tempData.loaded = false;
        tempData.list = [];
        lastId = null;
      }
      commit('setNewsRequesting', true);
      const response = await Vue.axios.get('/api/v1/activity/news', {
        params: {
          id: activityId,
          last_id: lastId,
          count,
        },
      });
      const rdata = response.data;
      commit('setNewsRequesting', false);
      if (rdata.status === 0) {
        commit('setNews', {
          activityId,
          news: rdata.data,
        });
        if (rdata.data.length < count) {
          commit('setNewsLoaded', {
            activityId,
            val: true,
          });
        }
      }
      return rdata;
    },
    /**
     * 获取战队详情
     * @param teamId
     */
    async getTeamDetail(
      { state, commit },
      { teamId, forceUpdate, formPath }: { teamId: number; forceUpdate?: boolean; formPath: string },
    ) {
      const detail = state.team.detail[teamId];
      const detailPromise = state.team.detailPromise[teamId];
      if ((!isUndef(detail) || !isUndef(detailPromise)) && !forceUpdate) {
        return detail || detailPromise;
      }
      const promise = Vue.axios.get('/api/v1/activity/team_info', {
        params: {
          id: teamId, form_path: formPath || location.href
        },
      });
      commit('setTeamDetailPromise', {
        teamId,
        data: promise,
      });
      const response = await promise;
      const rdata = response.data;
      if (rdata.status === 0) {
        const data: Activity.TeamDetail = rdata.data;
        commit('setTeamDetail', {
          teamId,
          data,
        });
      }
      return rdata;
    },
    /**
     * 获取其他的未满百的战队
     * @param teamId
     */
    async getAnotherTeamDetail(
      { state, commit },
      { activityId, teamId }: { activityId: string; teamId: number },
    ) {
      const response = await Vue.axios.get(
        '/api/v1/activity/another_not_full_team',
        {
          params: {
            id: activityId,
            team_id: teamId,
          },
        },
      );
      const rdata = response.data;
      if (rdata.status === 0) {
        const data: Activity.TeamDetail = rdata.data;
        if (data && data.id) {
          commit('setTeamDetail', {
            teamId: data.id,
            data,
          });
        }
      }
      return rdata;
    },
    /**
     * 获取活动套餐
     * @param {String} activiyId 活动id
     */
    async getPackage(
      { state, commit },
      {
        activityId,
        activitySpecialId,
        forceUpdate = false,
      }: {
        activityId: string;
        activitySpecialId?: string;
        forceUpdate?: boolean;
      },
    ) {
      let key = `${activityId}`;
      if (!isUndef(activitySpecialId)) {
        key = `${key}_${activitySpecialId}`;
      }
      if (state.activityPackages[key] && !forceUpdate) {
        return;
      }
      const response = await Vue.axios.get('/api/v1/activity/package_list', {
        params: {
          id: activityId,
          activity_special_id: activitySpecialId,
          v: 1,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        const data = rdata.data;
        commit('setActivityPackage', {
          key,
          data,
        });
        commit('Optional_goods', data );
      }
      return rdata;
    },
    // 更新数据

    /**
     * 升级套餐
     * @param participationId 订单id
     * @param packageId 套餐id
     */
    async changePackage(
      { state, commit, dispatch },
      { participationId, packageId, specifications },
    ) {
      const response = await Vue.axios.post(
        '/api/v1/participation/change_package',
        {
          id: participationId,
          package_id: packageId,
          specifications,
        },
      );
      const rdata = response.data;
      if (rdata.status === 0) {
        const data = rdata.data;
        if (data.type === 'upgrade') {
          const orderId = data.order.id;
          // 支付
          const payResponse = await Vue.axios.post(
            '/api/v1/participation/pay',
            {
              order_id: orderId,
              type: 'upgrade',
            },
          );
          const payRdata = payResponse.data;
          if (payRdata.status !== 0) {
            return {
              status: -1,
              message: payRdata.message,
            };
          }

          // 支付
          const ret = await chooseWXPay(payRdata.data.params);

          // 支付失败
          if (ret.status !== 'success') {
            return {
              status: -1,
              message: ret.status === 'cancel' ? '支付取消' : ret.result,
            };
          }

          // 支付成功
          let result: boolean | null = null;
          let msg: string | null = null;
          for (let i = 30; i > 0; i--) {
            const rdata = await dispatch(
              'payStatus',
              {
                orderId,
                type: 'upgrade_order',
              },
              {
                root: true,
              },
            );
            if (rdata.status !== 0 || !rdata.data) {
              // 错误，结束
              result = false;
              msg = rdata.message;
              break;
            } else if (rdata.data.finished === true) {
              // 成功
              result = true;
              break;
            } else if (rdata.data.finished === false) {
              // 等待
              await wait(1000);
            } else {
              result = false;
            }
          }

          if (result === false) {
            return {
              status: -1,
              message: msg || '发生了未知错误',
            };
          } else if (result === true) {
            return {
              status: 0,
              message: 'OK',
              data: orderId,
            };
          } else {
            return {
              status: -1,
              message: '系统繁忙，请稍后再试',
              data: orderId,
            };
          }
        } else {
          return {
            status: 0,
            ...data,
          };
        }
      } else {
        return {
          status: -1,
          message: rdata.message,
        };
      }
    },

    /**
     * 获取活动广告
     * @param {String} activiyId 活动id
     * @param {String} adType index/sign_in/team_manager
     */
    async getActivityAd(
      { state, commit },
      {
        activityId,
        adType,
        forceUpdate = false,
      }: { activityId: string; adType: Activity.AdType; forceUpdate?: boolean },
    ) {
      if (state.activityAd[activityId] && !forceUpdate) {
        return;
      }
      const response = await Vue.axios.get('/api/v1/activity/footer_images', {
        params: {
          id: activityId,
          type: adType,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        const data = rdata.data;
        commit('setActivityAd', {
          activityId,
          adType,
          data,
        });
      }
      return rdata;
    },
    /**
     * 获得战队邀请卡
     *
     * @param {string} activityId
     */
    async getPersonalInviteCard(
      { state, commit },
      { activityId, forceUpdate }: { activityId: string; forceUpdate?: boolean },
    ) {
      const temp = state.personalInviteCard[activityId];
      if (temp && !forceUpdate) {
        return;
      }
      const response = await Vue.axios.get(
      '/api/v1/activity/get_personal_invite_card_sync',
        {
          params: {
            activity_id: activityId,
          },
        },
      );
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setPersonalInviteCard', {
          activityId,
          card: rdata.data,
        });
      }
      return rdata;
    },
    async getTermCard(context, activityId) {
        // return {
        //     status: 0,
        //     data: {
        //         url: 'https://growth-test.oss-cn-beijing.aliyuncs.com/material/image/2021-02-15/182927-ZLxRF2.jpeg'
        //     }
        // }
        if(!activityId) {
            return
        }
        const response = await Vue.axios.get('/api/v1/activity/term_card', {
            params: {
                activity_id: activityId
            }
        });
        const rdata = response.data;
        return rdata;
    },
    /** 总招募数据 */
    async getRecruitStat({ state, commit }, { activityId }) {
      const response = await Vue.axios.get('/api/v1/monitor/recruit_stats', {
        params: {
          activity_id: activityId,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setRecruitStat', {
          activityId,
          recruitStat: rdata.data,
        });
      }
      return rdata;
    },
    /**
     * 获取锦囊接口
     */
    async getTipsList({ state, commit }, activityId) {
      const temp = state.activityTipsMap[activityId];
      if (temp) {
        return;
      }
      const response = await Vue.axios.get('/api/v1/participation/tips', {
        params: {
          activity_id: activityId,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setActivityTipsMap', {
          activityId,
          list: rdata.data,
        });
      }
      return rdata;
    },
    /**
     * 获得话术
     */
    async getShareWord({ state, commit }, { activityId, kind }) {
      const response = await Vue.axios.get('/api/v1/activity/speeches', {
        params: {
          id: activityId,
          kind,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setShareWord', {
          activityId,
          kind,
          shareWord: rdata.data,
        });
      }
      return rdata;
    },
    /**
     * 获得成就卡
     */
    async getSignInCard({ state, commit }, { signInId }) {
      const response = await Vue.axios.get(
        '/api/v1/participation/sign_in_card',
        {
          params: {
            id: signInId,
          },
        },
      );
      const rdata = response.data;
      return rdata;
    },
    /**
     * 获取活动礼包
     * @param {String} activiyId 活动id
     */
    async getGift(
      { state, commit },
      {
        activityId,
        forceUpdate = false,
      }: { activityId: string; forceUpdate?: boolean },
    ) {
      if (state.activityGift[activityId] && !forceUpdate) {
        return;
      }

      const response = await Vue.axios.get('/api/v1/gift/optional_gift_list', {
        params: {
          activity_id: activityId,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        const data = rdata.data;
        commit('setActivityGift', {
          activityId,
          data,
        });
      }
      return rdata;
    },
    /**
     * 确认选择礼包
     * @param {String} activiyId 活动id
     * @param {Number} giftId 礼品id
     */
    async chooseGift(
      { state, commit },
      { activityId, giftId }: { activityId: string; giftId: number },
    ) {
      const response = await Vue.axios.post(
        '/api/v1/gift/choose_optional_gift',
        {
          activity_id: activityId,
          gift_id: giftId,
        },
      );
      const rdata = response.data;
      return rdata;
    },
    /**
     * 获得category列表
     */
    async getCategoryList({ state, commit }, activityId) {
      const response = await Vue.axios.get(
        '/api/v1/activity/question_categories',
        {
          params: {
            id: activityId,
          },
        },
      );
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setCategoryList', rdata.data);
      }
      return rdata;
    },
    async getQuestionList(
      { state, commit },
      { activityId, categoryId, forceUpdate },
    ) {
      if (state.questionList[categoryId] && !forceUpdate) {
        return;
      }
      const response = await Vue.axios.get(
        '/api/v1/activity/question_and_answers',
        {
          params: {
            id: activityId,
            category_id: categoryId,
          },
        },
      );
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setQuestionList', {
          categoryId,
          questionList: rdata.data,
        });
      }
      return rdata;
    },
    /** 退款 */
    async participationRefund({ state, commit }, { activityId, reason }) {
      const response = await Vue.axios.post('/api/v1/participation/refund', {
        activity_id: activityId,
        reason,
      });
      const rdata = response.data;
      return rdata;
    },
    /**
     * 获取完赛的总结数据
     * @param {} activityId 活动id
     * @param {} userId 没有uid的情况下，将默认返回自己的数据
     */
    async getSummary({ state, commit }, { activityId, userId }) {
      if (!state.achievementMap[activityId]) {
        Vue.set(state.achievementMap, activityId, {});
      }
      const response = await Vue.axios.get('/api/v1/participation/summary', {
        params: {
          user_id: userId,
          activity_id: activityId,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setAchievementMap', {
          activityId,
          userId,
          summary: rdata.data,
        });
      }
      return rdata;
    },
    /**
     * 更新testimonial
     */
    async updateTestimonial(context, { activityId, userId, content }) {
      const response = await Vue.axios.post(
        '/api/v1/participation/create_recollection',
        {
          activity_id: activityId,
          user_id: userId,
          content,
        },
      );
      const rdata = response.data;
      return rdata;
    },
    /**
     * 获取用户是否报名
     */
    async getUserParticipation({ state, commit }, { activityId, userId }) {
      const response = await Vue.axios.get(
        '/api/v1/participation/user_has_sign_up',
        {
          params: {
            activity_id: activityId,
            user_id: userId,
          },
        },
      );
      return response.data;
    },
    /**
     * 针对于已经报名，但还没加入战队的用户
     */
    async joinTeam({ dispatch }, { activityId, teamId }) {
      const response = await Vue.axios.post('/api/v1/participation/join_team', {
        activity_id: activityId,
        team_id: teamId,
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        await dispatch('getBasicInfo', {
          activityId,
          forceUpdate: true,
        });
      }
      return rdata;
    },

    /**
     * 获取第十届完赛的总结数据
     * @param {} userId 没有uid的情况下，将默认返回自己的数据
     */
    async getReport10({ state, commit }, userId) {
      const response = await Vue.axios.get('/api/v1/finish_report/report_10', {
        params: {
          user_id: userId,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setReport10', rdata.data);
      }
      return rdata;
    },

    /**
     * 获得完赛成就卡
     */
    async getReport10Card({ state, commit }) {
      const response = await Vue.axios.get('/api/v1/finish_report/card_10');
      const rdata = response.data;
      return rdata;
    },

    /**
     * 获取第11届完赛的总结数据
     * @param {} userId 没有uid的情况下，将默认返回自己的数据
     */
    async getReport11({ state, commit }, userId) {
      const response = await Vue.axios.get('/api/v1/finish_report/report_11', {
        params: {
          user_id: userId,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setReport11', rdata.data);
      }
      return rdata;
    },
    /**
     * 获得完赛成就卡
     */
    async getReport11Card({ state, commit }) {
      const response = await Vue.axios.get('/api/v1/finish_report/card_11');
      const rdata = response.data;
      return rdata;
    },

    /**
     * 获取第11届完赛的总结数据
     * @param {} userId 没有uid的情况下，将默认返回自己的数据
     */
    async getFinishReport({ state, commit }, { activityId, userId }) {
      const response = await Vue.axios.get('/api/v1/finish_report/report', {
        params: {
          activity_id: activityId,
          user_id: userId,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setReport11', rdata.data);
      }
      return rdata;
    },
    /**
     * 获得完赛成就卡
     */
    async getFinishCard({ state, commit }, { activityId }) {
      const response = await Vue.axios.get('/api/v1/finish_report/card', {
        params: {
          activity_id: activityId,
        },
      });
      const rdata = response.data;
      return rdata;
    },

    /**
     * 获取用户肖像
     */
    async getRunners(
      { state, commit },
      { activityId, offset, count, forceUpdate = false },
    ) {
      if (state.runners.requesting || (state.runners.loaded && !forceUpdate)) {
        return;
      }
      if (offset === 0 && forceUpdate) {
        state.runners.list = [];
        state.runners.loaded = false;
        state.runners.requesting = false;
      }
      const end = offset + count;
      if (end <= state.runners.list.length) {
        return;
      }
      if (offset < state.runners.list.length) {
        offset = state.runners.list.length;
        count = end - offset;
      }
      commit('setRunnersRequest', true);
      const response = await Vue.axios.get('/api/v1/activity/user_avatars', {
        params: {
          activity_id: activityId,
          offset,
          count,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setRunners', rdata.data);
        if (rdata.data.length < count) {
          commit('setRunnersLoaded', true);
        }
      }
      commit('setRunnersRequest', false);
      return rdata;
    },

    /**
     * 获取用户当前队伍
     */
    async getCurrentTeam({ state, commit }, { userId, forceUpdate }) {
      if (state.currentTeam[userId] && !forceUpdate) {
        return;
      }
      const response = await Vue.axios.get(
        '/api/v1/participation/current_team',
        {
          params: {
            user_id: userId,
          },
        },
      );
      const rdata = response.data;
      if (rdata.status === 0) {
        commit('setCurrentTeam', {
          userId,
          team: rdata.data,
        });
      }
      return rdata;
    },
    /**
     * 获取跑友故事
     */
    async getStory(context, { offset, count, forceUpdate }) {
      if (context.state.story && !forceUpdate) {
        return;
      }
      const response = await Vue.axios.get('/api/v1/activity/story', {
        params: {
          offset,
          count,
        },
      });
      const rdata = response.data;
      if (rdata.status === 0) {
        context.commit('setStory', rdata.data);
      }
      return rdata;
    },
    async deductionCodeCheck(context, { code, activity_id }) {
      const response = await Vue.axios.get('/api/v1/deduction_code/check', {
        params: {
            code, activity_id
        },
      });
      const rdata = response.data;
      return rdata;
    },
  },
};

export default m;
